import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/layout/AppSidebar";
import { Header } from "@/components/layout/Header";
import { AuthProvider } from "@/hooks/useAuth";
import { AuthorizationProvider } from "@/contexts/AuthorizationContext";
import { ProtectedRoute } from "@/components/auth";
import { PERMISSIONS } from "@/types/permissions";
import { ThemeProvider } from "next-themes";

import Dashboard from "./pages/Dashboard";
import Pipeline from "./pages/Pipeline";
import PipelineParceiros from "./pages/PipelineParceiros";
import Clientes from "./pages/Clientes";
import Propostas from "./pages/Propostas";
import Instalacoes from "./pages/Instalacoes";
import Relatorios from "./pages/Relatorios";
import Calendario from "./pages/Calendario";
import Metas from "./pages/Metas";
import Territorios from "./pages/Territorios";
import Financeiro from "./pages/Financeiro";
import { EmpresaFinanceiraProvider } from "@/contexts/EmpresaFinanceiraContext";
import Configuracoes from "./pages/Configuracoes";
import ModulosFotovoltaicos from "./pages/ModulosFotovoltaicos";
import Inversores from "./pages/Inversores";
import Baterias from "./pages/Baterias";
import ModelosCarregadores from "./pages/ModelosCarregadores";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import Calculadora from "./pages/Calculadora";
import CarregadoresVeiculares from "./pages/CarregadoresVeiculares";
import AdequacoesReformas from "./pages/AdequacoesReformas";
import Dimensionamento from "./pages/Dimensionamento";
import Distribuidores from "./pages/Distribuidores";
import Contratos from "./pages/Contratos";
import Parceiros from "./pages/Parceiros";
import Vendedores from "./pages/Vendedores";
import DefinirSenha from "./pages/parceiro/DefinirSenha";
import DefinirSenhaVendedor from "./pages/vendedor/DefinirSenha";
import TesteAtivacaoParceiros from "./pages/TesteAtivacaoParceiros";
import RevisaoPrecificacaoAdmin from "./components/Dimensionamento/RevisaoPrecificacaoAdmin";
import Cadastros from "./pages/Cadastros";
import Tarefas from "./pages/Tarefas";
import Notificacoes from "./pages/Notificacoes";
import TermosDeUso from "./pages/TermosDeUso";
import PoliticaPrivacidade from "./pages/PoliticaPrivacidade";
import AssinaturaContratoPlataforma from "./pages/AssinaturaContratoPlataforma";
import AssinaturasPendentesSun7 from "./pages/AssinaturasPendentesSun7";
import NotasFiscais from "./pages/NotasFiscais";
import OrcamentosCarregadores from "./pages/OrcamentosCarregadores";
const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <AuthProvider>
        <AuthorizationProvider>
          <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/termos" element={<TermosDeUso />} />
              <Route path="/privacidade" element={<PoliticaPrivacidade />} />
              <Route path="/parceiro/definir-senha" element={<DefinirSenha />} />
              <Route path="/vendedor/definir-senha" element={<DefinirSenhaVendedor />} />
              <Route path="/teste-ativacao-parceiros" element={<TesteAtivacaoParceiros />} />
              <Route path="/assinatura-contrato-plataforma" element={<AssinaturaContratoPlataforma />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Dashboard />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/pipeline" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1 overflow-x-auto">
                          <Pipeline />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/pipeline-parceiros" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1 overflow-x-auto">
                          <PipelineParceiros />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/clientes" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Clientes />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/calculadora" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Calculadora />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/carregadores" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <CarregadoresVeiculares />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/adequacoes-reformas" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <AdequacoesReformas />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/dimensionamento" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1 overflow-x-hidden" style={{ maxWidth: '100vw', width: '100%', boxSizing: 'border-box', overflowX: 'hidden', minWidth: 0 }}>
                          <Dimensionamento />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/propostas" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background overflow-x-hidden">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen overflow-x-hidden">
                        <Header />
                        <main className="flex-1 overflow-x-hidden">
                          <Propostas />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/contratos" element={
                <ProtectedRoute requiredPermission="contracts:read">
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Contratos />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/tarefas" element={
                <ProtectedRoute requiredRole={['admin', 'financeiro']}>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Tarefas />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/distribuidores" element={
                <ProtectedRoute requiredPermission="distributors:read">
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Distribuidores />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/parceiros" element={
                <ProtectedRoute requiredPermission="parceiros:read">
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Parceiros />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/vendedores" element={
                <ProtectedRoute requiredPermission="vendedores:read">
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Vendedores />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/instalacoes" element={
                <ProtectedRoute blockedRole={['vendedor', 'parceiro']}>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Instalacoes />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/relatorios" element={
                <ProtectedRoute requiredPermission={PERMISSIONS.REPORTS.READ}>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Relatorios />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/calendario" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Calendario />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/metas" element={
                <ProtectedRoute requiredRole={['admin', 'representante']}>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Metas />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/territorios" element={
                <ProtectedRoute requiredRole={['admin', 'representante']}>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Territorios />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/financeiro" element={
                <ProtectedRoute requiredRole={['admin', 'representante']}>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <EmpresaFinanceiraProvider>
                            <Financeiro />
                          </EmpresaFinanceiraProvider>
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/configuracoes" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Configuracoes />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/modulos-fotovoltaicos" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <ModulosFotovoltaicos />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/inversores" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Inversores />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/baterias" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Baterias />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/modelos-carregadores" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <ModelosCarregadores />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/revisao-precificacao" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <RevisaoPrecificacaoAdmin />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/cadastros" element={
                <ProtectedRoute requiredRole={['admin']}>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Cadastros />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/assinaturas-pendentes-sun7" element={
                <ProtectedRoute requiredRole={['admin', 'financeiro']}>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <AssinaturasPendentesSun7 />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/notas-fiscais" element={
                <ProtectedRoute requiredRole={['admin', 'financeiro']}>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <NotasFiscais />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/orcamentos-carregadores" element={
                <ProtectedRoute requiredRole={['admin', 'financeiro']}>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <OrcamentosCarregadores />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/notificacoes" element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <div className="min-h-screen flex w-full bg-background">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-1">
                          <Notificacoes />
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              } />
              <Route path="/" element={<Navigate to="/auth" replace />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
          </TooltipProvider>
        </AuthorizationProvider>
      </AuthProvider>
    </ThemeProvider>
    </QueryClientProvider>
);

export default App;
